// @ts-check
import { ShowAlert } from "Utils/CommonFunctions";
import SettingsRequests from "Utils/Requests/SettingsRequests";
import { action, makeAutoObservable } from "mobx";
import React from "react";

/**
 * @typedef {Object} SettingsExtended
 * @property {boolean} settingsChanged
 * @property {{notifyNewVersion: boolean, notifyNewVersionChanged: boolean}} notifyDeviceInfo
 */

class UsersServerSettingsStore {
    /** @type {GetUsersServerSettingsResponse & SettingsExtended} */
    settings;
    settingsLoading;

    constructor() {
        makeAutoObservable(this);

        this.settings = {
            settingsChanged: false,
            logoFileId: null,
            logoFileData: null,
            backgroundFileId: null,
            backgroundFileData: null,
            enablePushNotification: false,
            prohibitSignNotAvailableEdsFile: false,
            customOrganisationName: null,
            addToStoplistWithoutEDSstamp: true,
            mcDisableSslChainValidation: false,
            notifyDeviceInfo: {
                totalDevicesCount: 0,
                notifiableDevicesCount: 0,
                notifyNewVersion: false,
                notifyNewVersionChanged: false
            }
        };
        this.settingsLoading = false;
    }

    LoadSettings() {
        if (this.settingsLoading)
            return;

        this.settingsLoading = true;

        SettingsRequests.GetUsersServerSettings()
            .then(action(result => {
                Object.assign(this.settings, result);
                this.settings.settingsChanged = false;
                this.settings.notifyDeviceInfo.notifyNewVersion = this.settings.notifyDeviceInfo.notifiableDevicesCount > 0;
                this.settings.notifyDeviceInfo.notifyNewVersionChanged = false;
            }))
            .finally(action(() => this.settingsLoading = false));
    }

    SaveSettings() {
        if (this.settingsLoading)
            return;

        this.settingsLoading = true;

        /** @type {SaveUsersServerSettingsRequest} */
        let dataToSend = {
            backgroundFileId: this.settings.backgroundFileId,
            backgroundFileData: this.settings.backgroundFileData,
            logoFileId: this.settings.logoFileId,
            logoFileData: this.settings.logoFileData,
            enablePushNotification: this.settings.enablePushNotification,
            prohibitSignNotAvailableEdsFile: this.settings.prohibitSignNotAvailableEdsFile,
            customOrganisationName: this.settings.customOrganisationName,
            addToStoplistWithoutEDSstamp: this.settings.addToStoplistWithoutEDSstamp,
            mcDisableSslChainValidation: this.settings.mcDisableSslChainValidation,
            notifyAboutNewVersion: this.settings.notifyDeviceInfo.notifyNewVersionChanged ?
                this.settings.notifyDeviceInfo.notifyNewVersion : null
        };

        SettingsRequests.SaveUsersServerSettings(dataToSend)
            .then(action(result => {
                if (result.error) {
                    ShowAlert('error',
                        <div>
                            При сохранении настроек произошла ошибка.<br />
                            {(result.error.errorText == null ? 'Убедитесь что СМР работает и повторите попытку.' : result.error.errorText)}
                        </div>
                    );
                    return;
                }

                Object.assign(this.settings.notifyDeviceInfo, result.notifyDevicesInfo);
                this.settings.settingsChanged = false;
                this.settings.notifyDeviceInfo.notifyNewVersion = this.settings.notifyDeviceInfo.notifiableDevicesCount > 0;
                this.settings.notifyDeviceInfo.notifyNewVersionChanged = false;
                ShowAlert('success', 'Настройки успешно сохранены.');
            }))
            .finally(action(() => this.settingsLoading = false));
    }

    /**
     * @param {boolean} value
     */
    SetNotifyNewVersion(value) {
        this.settings.notifyDeviceInfo.notifyNewVersion = value;
        this.settings.notifyDeviceInfo.notifyNewVersionChanged = true;
        this.settings.settingsChanged = true;
    }

    /**
     * @param {boolean} value
     */
    SetPushNotification(value) {
        this.settings.enablePushNotification = value;
        this.settings.settingsChanged = true;
    }

    /**
     * @param {boolean} value
     */
    SetProhibitSignNotAvailableEdsFile(value) {
        this.settings.prohibitSignNotAvailableEdsFile = value;
        this.settings.settingsChanged = true;
    }

    /**
     * @param {string} value
     */
    SetCustomOrganization(value) {
        this.settings.customOrganisationName = value;
        this.settings.settingsChanged = true;
    }

    /**
     * @param {boolean} value
     */
    SetAddToStoplistWithoutEDSstamp(value) {
        this.settings.addToStoplistWithoutEDSstamp = value;
        this.settings.settingsChanged = true;
    }

    /**
     * @param {boolean} value 
     */
    SetMcDisableSslChainValidation(value) {
        this.settings.mcDisableSslChainValidation = value;
        this.settings.settingsChanged = true;
    };

    /**
     * @param {string | null} value
     */
    SetLogo(value) {
        this.settings.logoFileData = value;
        this.settings.logoFileId = null;
        this.settings.settingsChanged = true;
    }

    /**
     * @param {string | null} value
     */
    SetBackground(value) {
        this.settings.backgroundFileData = value;
        this.settings.backgroundFileId = null;
        this.settings.settingsChanged = true;
    }
}

export default new UsersServerSettingsStore();